import { CSSProperties, useState } from 'react';
import { motion } from 'framer-motion';
import { ProviderId, ServiceType } from '../../types';
import { PROVIDERS } from '../../constants';
import Card from '../Card';

interface Step2Props {
  selectedService: ServiceType;
  selectedProvider: ProviderId | null;
  onSelectProvider: (provider: ProviderId) => void;
}

export default function Step2ProviderSelection({ selectedService, selectedProvider, onSelectProvider }: Step2Props) {
  const [showManualOptions, setShowManualOptions] = useState(false);

  const handleToggleManual = () => {
    setShowManualOptions((prev) => {
      const next = !prev;

      if (!next && selectedProvider && selectedProvider !== 'auto') {
        onSelectProvider('auto');
      }

      return next;
    });
  };

  // Filter providers based on service type
  // Vinil is only available for support and kickoff, not demos
  // Shawn and Ramon are only available for training (support) meetings
  const availableProviders = PROVIDERS.filter(provider => {
    if (selectedService === 'demo' && provider.id === 'vinil') {
      return false;
    }
    if (selectedService !== 'support' && (provider.id === 'shawn' || provider.id === 'ramon')) {
      return false;
    }
    return true;
  });
  const containerStyles: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-xl)',
    width: '100%',
    maxWidth: '900px',
    margin: '0 auto'
  };

  const headerStyles: CSSProperties = {
    textAlign: 'center',
    marginBottom: 'var(--spacing-lg)'
  };

  const titleStyles: CSSProperties = {
    fontSize: '2rem',
    fontWeight: 700,
    color: 'var(--color-secondary)',
    marginBottom: 'var(--spacing-sm)'
  };

  const subtitleStyles: CSSProperties = {
    fontSize: '1.125rem',
    color: 'var(--color-text-secondary)',
    lineHeight: 1.6
  };

  const gridStyles: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 'var(--spacing-lg)',
    width: '100%'
  };

  const autoCardStyles: CSSProperties = {
    gridColumn: '1 / -1',
    background: 'linear-gradient(135deg, var(--na-teal) 0%, var(--na-teal-light) 100%)'
  };

  const cardContentStyles: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 'var(--spacing-md)',
    padding: 'var(--spacing-md)'
  };

  const avatarStyles: CSSProperties = {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, var(--na-navy) 0%, var(--na-navy-light) 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontSize: '2rem',
    fontWeight: 700
  };

  const providerNameStyles: CSSProperties = {
    fontSize: '1.25rem',
    fontWeight: 700,
    color: 'var(--color-secondary)',
    textAlign: 'center'
  };

  const titleTextStyles: CSSProperties = {
    fontSize: '0.875rem',
    color: 'var(--color-text-secondary)',
    textAlign: 'center'
  };

  const autoContentStyles: CSSProperties = {
    ...cardContentStyles,
    padding: 'var(--spacing-xl)'
  };

  const autoTextStyles: CSSProperties = {
    fontSize: '1.125rem',
    fontWeight: 600,
    color: 'white',
    textAlign: 'center'
  };

  const autoSubtextStyles: CSSProperties = {
    fontSize: '0.9375rem',
    color: 'rgba(255, 255, 255, 0.9)',
    textAlign: 'center'
  };

  const toggleContainerStyles: CSSProperties = {
    textAlign: 'center',
    marginTop: 'var(--spacing-lg)'
  };

  const toggleButtonStyles: CSSProperties = {
    background: 'transparent',
    border: 'none',
    color: 'var(--color-primary)',
    fontWeight: 600,
    cursor: 'pointer',
    fontSize: '0.9375rem',
    textDecoration: 'underline'
  };

  const manualHintStyles: CSSProperties = {
    fontSize: '0.875rem',
    color: 'var(--color-text-secondary)',
    marginTop: 'var(--spacing-xs)'
  };

  return (
    <div style={containerStyles}>
      <motion.div
        style={headerStyles}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 style={titleStyles}>Choose Your Specialist</h1>
        <p style={subtitleStyles}>
          Select a team member or let us assign the next available specialist
        </p>
      </motion.div>

      <div style={gridStyles}>
        {/* Auto-assign option */}
        <motion.div
          style={{ gridColumn: '1 / -1' }}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Card
            onClick={() => onSelectProvider('auto')}
            selected={selectedProvider === 'auto'}
            style={autoCardStyles}
          >
            <div style={autoContentStyles}>
              <span style={{ fontSize: '0.8125rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(255,255,255,0.8)' }}>
                Recommended
              </span>
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 2L2 7l10 5 10-5-10-5z" />
                <path d="M2 17l10 5 10-5M2 12l10 5 10-5" />
              </svg>
              <p style={autoTextStyles}>Next Available Specialist</p>
              <p style={autoSubtextStyles}>
                We'll automatically assign you to the next available team member
              </p>
            </div>
          </Card>
        </motion.div>

       {showManualOptions && availableProviders.map((provider, index) => (
         <motion.div
           key={provider.id}
           initial={{ opacity: 0, y: 30 }}
           animate={{ opacity: 1, y: 0 }}
           transition={{ duration: 0.4, delay: (index + 1) * 0.1 }}
         >
           <Card
             onClick={() => onSelectProvider(provider.id)}
             selected={selectedProvider === provider.id}
              style={{
                borderColor: selectedProvider === provider.id ? 'var(--color-primary)' : undefined,
                boxShadow: selectedProvider === provider.id ? 'var(--shadow-lg)' : undefined
              }}
            >
              <div style={cardContentStyles}>
                <div style={avatarStyles}>
                  {provider.name.charAt(0)}
                </div>
                <h3 style={providerNameStyles}>{provider.name}</h3>
                <p style={titleTextStyles}>{provider.title}</p>
              </div>
            </Card>
          </motion.div>
        ))}
      </div>

      <div style={toggleContainerStyles}>
        <button type="button" onClick={handleToggleManual} style={toggleButtonStyles}>
          {showManualOptions ? 'Hide manual selection' : 'Prefer someone specific? Choose manually'}
        </button>
        {!showManualOptions && (
          <p style={manualHintStyles}>
            We&apos;ll route you to the next available specialist, but you can still pick a team member if needed.
          </p>
        )}
      </div>
    </div>
  );
}
